// Conversation — a space shared by the devices of several people: a direct conversation (me and
// one contact, phase 3) or a group (phase 4). Same journal, same messages, same names.
//
// Messages and group events live in the conversation journal. Accepted payloads are also kept
// decrypted in the local store (as notes keep their updates), so opening the conversation does
// not re-decrypt the whole journal. Display order: author clock, then author id.
//
// A device belongs to a person only if its attestation verifies (contacts/identity.ts): a
// contact's device cannot add a fake device "in my name" and then edit my messages. Envelope
// effects are applied one at a time, after the attestation checks they depend on.
//
// Rules (journal policy, docs/phase-4-groupes.md): anyone may revoke their own devices; only the
// group's creator may revoke someone else's, rename the group or remove a member; a member.leave
// must come from that person's own device. In a direct conversation nobody can remove the other.
//
// Events: 'change' (messages, members or statuses changed).

import { randomHex } from '../core/ids';
import { checkAttestation } from './identity';
import { Journal, type Member } from '../space/journal';
import type { Payload } from '../space/envelope';
import type { DeviceRecord, LocalDevice, SpaceSecrets } from '../space/keys';
import type { Store } from '../space/store';
import type { SpaceSync } from '../space/sync';

export const MAX_TEXT = 16 * 1024;
const LOG = 'msg'; // key of the decrypted-payload log in the store
const LOGGED = new Set(['profile.set', 'group.meta', 'member.leave', 'member.remove']);

export type Person = { identity: string; name: string };
export type Kind = 'direct' | 'group';

export type Message = {
  id: string;
  device: string;
  person: string | null;
  text: string;
  ts: number;
  seq: number;
  replyTo?: string;
  edited?: number;
  deleted?: boolean;
  reactions: Map<string, Set<string>>;
};

/** A group event shown in the thread ("Alice a ajouté Bob"…). Names are resolved at display. */
export type Notice = { ts: number; kind: 'added' | 'left' | 'removed' | 'renamed'; actor: string | null; target: string | null; name?: string };

export type DeliveryStatus = 'pending' | 'delivered' | 'read';

type Logged = Extract<Payload, { type: `msg.${string}` | 'profile.set' | 'group.meta' | 'member.leave' | 'member.remove' }>;

export class Conversation extends EventTarget {
  readonly journal: Journal;
  readonly messages = new Map<string, Message>();
  /** Read marks: device → time up to which it has read. */
  readonly reads = new Map<string, number>();
  sync: SpaceSync | null = null;
  /** Names people chose for themselves (profile.set from a verified device of theirs). */
  readonly profiles = new Map<string, { name: string; ts: number }>();
  /** Group name (last writer wins, creator only). */
  groupName: { name: string; ts: number } = { name: '', ts: 0 };
  /** People who left or were removed (they stay listed in the history). */
  readonly gone = new Map<string, 'left' | 'removed'>();
  readonly #notices: Notice[] = [];
  /** My private nickname for a person, if any (provided by the conversations manager). */
  aliasFor: (person: string) => string | null = () => null;
  /** Device → person, for devices whose attestation verified. */
  readonly #persons = new Map<string, string>();
  readonly #checked = new Map<string, Promise<string | null>>();
  #tasks: Promise<void> = Promise.resolve();

  constructor(
    readonly store: Store,
    device: LocalDevice,
    secrets: SpaceSecrets,
    readonly me: string,
    /** The contact, for a direct conversation; null for a group. */
    readonly peer: Person | null,
    readonly kind: Kind = 'direct',
    initialName = '',
  ) {
    super();
    this.groupName = { name: initialName, ts: 0 };
    this.journal = new Journal(store, device, secrets);
    this.journal.policy = (ctx) => this.#policy(ctx.author, ctx.payload);
    this.journal.addEventListener('envelope', (e) => {
      const { envelope, payload } = (e as CustomEvent).detail as { envelope: { author: string; seq: number }; payload: Payload };
      this.#tasks = this.#tasks
        .then(async () => {
          if (payload.type === 'device.add') await this.#verify(payload.device.id);
          if (payload.type.startsWith('msg.') || LOGGED.has(payload.type)) {
            await store.putUpdate({ note: LOG, author: envelope.author, seq: envelope.seq, ts: payload.ts, update: new TextEncoder().encode(JSON.stringify(payload)) });
            this.#apply(envelope.author, envelope.seq, payload as Logged);
          }
          this.#changed();
        })
        .catch((err) => console.error('conversation', err));
    });
  }

  get id(): string {
    return this.journal.spaceId;
  }

  async load(): Promise<void> {
    await this.journal.load();
    for (const id of this.journal.members.keys()) await this.#verify(id);
    const dec = new TextDecoder();
    for (const u of await this.store.updatesFor(LOG)) this.#apply(u.author, u.seq, JSON.parse(dec.decode(u.update)) as Logged);
  }

  /** Resolves once every accepted envelope so far has been applied. */
  settled(): Promise<void> {
    return this.#tasks;
  }

  // ---- people ----

  personOf(device: string): string | null {
    return this.#persons.get(device) ?? null;
  }

  /** Active devices of a person (verified attestations only). */
  devicesOf(person: string): string[] {
    return this.journal.activeMembers().map((m) => m.record.id).filter((d) => this.#persons.get(d) === person);
  }

  /** People with at least one active device here, me included. */
  persons(): string[] {
    return [...new Set(this.journal.activeMembers().map((m) => this.#persons.get(m.record.id)).filter((p): p is string => !!p))];
  }

  others(): string[] {
    return this.persons().filter((p) => p !== this.me);
  }

  /** The person of the founding device: the group's administrator. */
  get creator(): string | null {
    const genesis = [...this.journal.members.values()].find((m) => m.addedBy === m.record.id);
    return genesis ? this.personOf(genesis.record.id) : null;
  }

  isMine(m: Message): boolean {
    return m.person === this.me;
  }

  /** How to call a person here: my nickname, else the name they chose, else what I knew of them. */
  nameOf(person: string | null): string {
    if (!person) return 'appareil inconnu';
    if (person === this.me) return this.profiles.get(person)?.name ?? 'moi';
    return this.aliasFor(person) ?? this.profiles.get(person)?.name ?? (person === this.peer?.identity ? this.peer.name : 'membre');
  }

  peerName(): string {
    return this.peer ? this.nameOf(this.peer.identity) : this.title();
  }

  /** What the list and the header show. */
  title(): string {
    if (this.kind === 'direct') return this.peerName();
    return this.groupName.name || this.others().map((p) => this.nameOf(p)).join(', ') || 'Groupe';
  }

  // ---- thread ----

  /** Messages in display order, deleted ones included (shown as tombstones). */
  list(): Message[] {
    return [...this.messages.values()].sort((a, b) => a.ts - b.ts || (a.device < b.device ? -1 : 1));
  }

  /** Group events, including additions derived from the member list. */
  notices(): Notice[] {
    if (this.kind !== 'group') return [];
    const added: Notice[] = [];
    const seen = new Set<string>();
    for (const m of [...this.journal.members.values()].sort((a, b) => a.addedAt - b.addedAt)) {
      const person = this.personOf(m.record.id);
      const by = this.personOf(m.addedBy);
      if (!person || seen.has(person)) continue;
      seen.add(person);
      if (m.addedBy !== m.record.id && by !== person) added.push({ ts: m.addedAt, kind: 'added', actor: by, target: person });
    }
    return [...added, ...this.#notices].sort((a, b) => a.ts - b.ts);
  }

  /** Messages and notices interleaved by time. */
  timeline(): (Message | Notice)[] {
    return [...this.list(), ...this.notices()].sort((a, b) => a.ts - b.ts);
  }

  last(): Message | undefined {
    const all = this.list().filter((m) => !m.deleted);
    return all[all.length - 1];
  }

  /** Messages from others newer than what any of my devices marked read. */
  unread(): number {
    const mine = [...this.reads].filter(([d]) => this.personOf(d) === this.me).map(([, t]) => t);
    const upTo = mine.length ? Math.max(...mine) : 0;
    return this.list().filter((m) => !m.deleted && !this.isMine(m) && m.ts > upTo).length;
  }

  /** For each other person: has one of their devices got / read this message? */
  receipts(m: Message): { delivered: number; read: number; total: number } {
    let delivered = 0;
    let read = 0;
    const others = this.others();
    for (const person of others) {
      const devices = this.devicesOf(person);
      const hasRead = devices.some((d) => (this.reads.get(d) ?? 0) >= m.ts);
      if (hasRead) read++;
      if (hasRead || (this.sync && devices.some((d) => this.sync!.holds(d, m.device, m.seq)))) delivered++;
    }
    return { delivered, read, total: others.length };
  }

  status(m: Message): DeliveryStatus {
    const r = this.receipts(m);
    if (r.total && r.read === r.total) return 'read';
    if (r.total && r.delivered === r.total) return 'delivered';
    return 'pending';
  }

  // ---- writing ----

  async post(text: string, replyTo?: string): Promise<string> {
    const id = randomHex(12);
    await this.journal.append({ type: 'msg.post', ts: Date.now(), id, text: text.slice(0, MAX_TEXT), replyTo });
    return id;
  }

  async edit(id: string, text: string): Promise<void> {
    await this.journal.append({ type: 'msg.edit', ts: Date.now(), id, text: text.slice(0, MAX_TEXT) });
  }

  async remove(id: string): Promise<void> {
    await this.journal.append({ type: 'msg.delete', ts: Date.now(), id });
  }

  async react(id: string, emoji: string, on: boolean): Promise<void> {
    await this.journal.append({ type: 'msg.react', ts: Date.now(), id, emoji: emoji.slice(0, 8), on });
  }

  async rename(name: string): Promise<void> {
    await this.journal.append({ type: 'group.meta', ts: Date.now(), name: name.trim().slice(0, 80) });
  }

  /** Marks everything up to the newest message as read on this device (only if it moves forward). */
  async markRead(): Promise<void> {
    const newest = this.list().at(-1)?.ts ?? 0;
    if (newest > (this.reads.get(this.journal.device.id) ?? 0) && this.journal.isMember) {
      await this.journal.append({ type: 'msg.read', ts: Date.now(), upTo: newest });
    }
  }

  #changed(): void {
    this.dispatchEvent(new Event('change'));
  }

  async #verify(device: string): Promise<void> {
    const person = await this.#personFor(this.journal.members.get(device)?.record);
    if (person) this.#persons.set(device, person);
  }

  /** Verified person of a device record (cached; the policy needs it before the listener runs). */
  #personFor(record: DeviceRecord | undefined): Promise<string | null> {
    if (!record?.person) return Promise.resolve(null);
    let p = this.#checked.get(record.id);
    if (!p) {
      p = checkAttestation(record).then((ok) => (ok ? record.person! : null));
      this.#checked.set(record.id, p);
    }
    return p;
  }

  async #policy(author: Member, payload: Payload): Promise<boolean> {
    const person = await this.#personFor(author.record);
    const creatorRecord = [...this.journal.members.values()].find((m) => m.addedBy === m.record.id)?.record;
    const creator = await this.#personFor(creatorRecord);
    switch (payload.type) {
      case 'device.revoke': {
        const target = await this.#personFor(this.journal.members.get(payload.device)?.record);
        return (!!person && target === person) || (this.kind === 'group' && !!person && person === creator);
      }
      case 'group.meta':
      case 'member.remove':
        return this.kind === 'group' && !!person && person === creator;
      case 'member.leave':
        return !!person && payload.person === person;
      default:
        return true;
    }
  }

  /** Applies a logged payload. Only a message's author (same person) may edit or delete it. */
  #apply(device: string, seq: number, p: Logged): void {
    const person = this.personOf(device);
    if (p.type === 'msg.post') {
      if (this.messages.has(p.id) || typeof p.text !== 'string') return;
      this.messages.set(p.id, { id: p.id, device, person, text: p.text.slice(0, MAX_TEXT), ts: p.ts, seq, replyTo: p.replyTo, reactions: new Map() });
    } else if (p.type === 'msg.edit' || p.type === 'msg.delete') {
      const m = this.messages.get(p.id);
      if (!m || m.person !== person || (m.edited && m.edited > p.ts)) return;
      if (p.type === 'msg.edit' && !m.deleted) {
        m.text = String(p.text).slice(0, MAX_TEXT);
        m.edited = p.ts;
      } else if (p.type === 'msg.delete') {
        m.deleted = true;
        m.text = '';
      }
    } else if (p.type === 'msg.react') {
      const m = this.messages.get(p.id);
      if (!m || !person) return;
      const who = m.reactions.get(p.emoji) ?? new Set<string>();
      if (p.on) who.add(person);
      else who.delete(person);
      if (who.size) m.reactions.set(p.emoji, who);
      else m.reactions.delete(p.emoji);
    } else if (p.type === 'msg.read') {
      if (p.upTo > (this.reads.get(device) ?? 0)) this.reads.set(device, p.upTo);
    } else if (p.type === 'profile.set') {
      if (person && typeof p.name === 'string' && p.ts > (this.profiles.get(person)?.ts ?? 0)) this.profiles.set(person, { name: p.name.slice(0, 60), ts: p.ts });
    } else if (p.type === 'group.meta') {
      if (p.ts > this.groupName.ts) this.groupName = { name: String(p.name).slice(0, 80), ts: p.ts };
      this.#notices.push({ ts: p.ts, kind: 'renamed', actor: person, target: null, name: String(p.name).slice(0, 80) });
    } else if (p.type === 'member.leave') {
      this.gone.set(p.person, 'left');
      this.#notices.push({ ts: p.ts, kind: 'left', actor: p.person, target: p.person });
    } else if (p.type === 'member.remove') {
      this.gone.set(p.person, 'removed');
      this.#notices.push({ ts: p.ts, kind: 'removed', actor: person, target: p.person });
    }
  }
}
